import { saveAs } from 'file-saver';
import { fabric } from 'fabric';

export const generateQRCodeUrl = (url: string): string => {
  // Use the QR Code API to generate a QR code image with even higher resolution
  return `https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=${encodeURIComponent(url)}`;
};

export const createStyledQRCode = async (qrCodeUrl: string, businessName: string, serviceLink: string) => {
  try {
    // Fetch the QR code image
    const response = await fetch(qrCodeUrl);
    const blob = await response.blob();
    const qrCodeImageUrl = URL.createObjectURL(blob);

    // Use a 9:16 aspect ratio for phone wallpaper (e.g., 1125x2000)
    const canvasWidth = 1125;
    const canvasHeight = 2000;
    const canvas = new fabric.Canvas(document.createElement('canvas'), {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: 'transparent'
    });

    // Gradient background
    const gradientRect = new fabric.Rect({
      left: 0,
      top: 0,
      width: canvas.width,
      height: canvas.height,
      fill: new fabric.Gradient({
        type: 'linear',
        coords: { x1: 0, y1: 0, x2: canvas.width, y2: 0 },
        colorStops: [
          { offset: 0, color: '#FF1493' },
          { offset: 1, color: '#FF8C00' }
        ]
      }),
      rx: 64,
      ry: 64
    });
    canvas.add(gradientRect);

    // Top margin
    let y = 120;

    // SCAN ME text
    const scanMeText = new fabric.Text("SCAN ME", {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 140,
      fontWeight: 'bold',
      fill: '#FFFFFF',
      textAlign: 'center',
      left: canvas.width / 2,
      top: y,
      originX: 'center',
      shadow: new fabric.Shadow({
        color: 'rgba(0,0,0,0.2)',
        blur: 8,
        offsetX: 4,
        offsetY: 4
      })
    });
    canvas.add(scanMeText);
    y += 160;

    // TO VISIT OUR WEBSITE text
    const visitText = new fabric.Text("TO VISIT OUR WEBSITE", {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 64,
      fill: '#FFFFFF',
      textAlign: 'center',
      left: canvas.width / 2,
      top: y,
      originX: 'center',
      shadow: new fabric.Shadow({
        color: 'rgba(0,0,0,0.1)',
        blur: 6,
        offsetX: 2,
        offsetY: 2
      })
    });
    canvas.add(visitText);
    y += 100;

    // Center QR code block vertically in the remaining space
    const qrBlockHeight = 700 + 80; // QR size + margin
    const qrBlockY = (canvas.height - y - 350 - qrBlockHeight) / 2 + y; // 350 is for text/logo below

    // QR code background
    const qrBackground = new fabric.Rect({
      left: canvas.width / 2 - 350,
      top: qrBlockY,
      width: 700,
      height: 700,
      fill: '#FFFFFF',
      rx: 64,
      ry: 64,
      shadow: new fabric.Shadow({
        color: 'rgba(0,0,0,0.15)',
        blur: 16,
        offsetX: 0,
        offsetY: 8
      })
    });
    canvas.add(qrBackground);

    // Load QR code image promise
    const loadQRImagePromise = new Promise((resolve) => {
      fabric.Image.fromURL(qrCodeImageUrl, (qrImg) => {
        const qrSize = 600;
        qrImg.set({
          left: canvas.width / 2 - qrSize / 2,
          top: qrBlockY + 50,
          scaleX: qrSize / (qrImg.width || 800),
          scaleY: qrSize / (qrImg.height || 800)
        });
        canvas.add(qrImg);
        resolve(true);
      }, { crossOrigin: 'anonymous' });
    });

    // Business name text
    const businessNameText = new fabric.Text(businessName, {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 80,
      fontWeight: 'bold',
      fill: '#FFFFFF',
      textAlign: 'center',
      left: canvas.width / 2,
      top: qrBlockY + 750,
      originX: 'center',
      shadow: new fabric.Shadow({
        color: 'rgba(0,0,0,0.2)',
        blur: 8,
        offsetX: 4,
        offsetY: 4
      })
    });
    canvas.add(businessNameText);

    // Gebeya logo with more vertical spacing and larger size
    const loadLogoPromise = new Promise((resolve) => {
      fabric.Image.fromURL("/lovable-uploads/14afcb65-2dcb-477c-8c08-3ae4cd079ee7.png", (logoImg) => {
        const logoWidth = 320;
        const logoHeight = logoWidth * (logoImg.height || 50) / (logoImg.width || 150);
        logoImg.set({
          left: canvas.width / 2,
          top: qrBlockY + 750 + 160,
          scaleX: logoWidth / (logoImg.width || 150),
          scaleY: logoHeight / (logoImg.height || 50),
          originX: 'center',
          originY: 'top'
        });
        canvas.add(logoImg);
        resolve(true);
      }, { crossOrigin: 'anonymous' });
    });

    await Promise.all([loadQRImagePromise, loadLogoPromise]);
    canvas.renderAll();

    return new Promise((resolve, reject) => {
      const canvasElement = canvas.getElement();
      canvasElement.toBlob((blob) => {
        if (blob) {
          saveAs(blob, `${businessName.replace(/\s+/g, '-').toLowerCase()}-qr-wallpaper.png`);
          resolve(true);
        } else {
          console.error("Failed to generate blob from canvas");
          reject(new Error("Failed to generate image"));
        }
      }, 'image/png', 1.0);
      URL.revokeObjectURL(qrCodeImageUrl);
    });
  } catch (error) {
    console.error('Error creating styled QR code:', error);
    throw error;
  }
};
